// @ts-check

/**
 * @typedef {Object} PushRuntimeConfig
 * @property {string} appId
 * @property {string} applicationServerKey
 * @property {string} serviceWorkerUrl
 * @property {string} serviceWorkerScope
 * @property {string} signupEndpoint
 * @property {{title: string, note: string, allowLabel: string, denyLabel: string, savingLabel: string, errorLabel: string, delayMs: number, idleTimeoutMs: number}} modal
 * @property {{path: string, names: {request: string, device: string, subscriber: string}, requestDeniedDays: number, requestSubscribedDays: number, deviceDays: number, subscriberDays: number}} cookies
 * @property {{subscribeWithUs: string}} messages
 */

/**
 * @typedef {Window & typeof globalThis & {
 *   spns_getCookie?: (name: string) => string,
 *   spns_requestPushPermission?: () => Promise<PushSubscription | null>,
 *   smpush_subscribe?: () => Promise<PushSubscription | null>
 * }} PushRuntimeWindow
 */

const globalWindow = /** @type {PushRuntimeWindow} */ (window);
const configElement = document.getElementById('spns-push-config');

if (!configElement?.textContent) {
	throw new Error('StarAtlas Push: runtime configuration is missing.');
}

/** @type {PushRuntimeConfig} */
const config = JSON.parse(configElement.textContent);
const LEGACY_COOKIE_NAMES = {
	request: 'smart_push_smio_request',
	device: 'smart_push_device_token',
	subscriber: 'smart_push_subscriber_id',
};
const COOKIE_REQUEST = config.cookies.names.request;
const COOKIE_DEVICE = config.cookies.names.device;
const COOKIE_SUBSCRIBER = config.cookies.names.subscriber;
const STYLE_ID = 'spns-push-runtime-style';
const MODAL_ID = 'smart_push_smio_window';

/** @type {ServiceWorkerRegistration | null} */
let serviceWorkerRegistration = null;
/** @type {HTMLElement | null} */
let modalElement = null;
/** @type {HTMLElement | null} */
let previousFocus = null;

/**
 * @param {string} name
 * @returns {string}
 */
function resolveCookieName(name) {
	if (name === LEGACY_COOKIE_NAMES.request) return COOKIE_REQUEST;
	if (name === LEGACY_COOKIE_NAMES.device) return COOKIE_DEVICE;
	if (name === LEGACY_COOKIE_NAMES.subscriber) return COOKIE_SUBSCRIBER;
	return name;
}

/**
 * @param {string} name
 * @returns {string}
 */
function getCookie(name) {
	const prefix = `${resolveCookieName(name)}=`;
	const item = (document.cookie || '')
		.split(';')
		.map((part) => part.trim())
		.find((part) => part.startsWith(prefix));

	if (!item) {
		return '';
	}

	const value = item.slice(prefix.length);
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
}

/**
 * @param {string} name
 * @param {string} value
 * @param {number} days
 */
function setCookie(name, value, days) {
	const expires = new Date(Date.now() + days * 86400000).toUTCString();
	document.cookie = `${resolveCookieName(name)}=${encodeURIComponent(value)}; Expires=${expires}; Path=${config.cookies.path}; SameSite=Lax; Secure`;
}

/** @param {string} name */
function deleteCookie(name) {
	setCookie(name, '', -1);
}

/**
 * @param {string} name
 * @param {Record<string, unknown>} data
 */
function emit(name, data = {}) {
	document.dispatchEvent(new CustomEvent(name, {
		detail: {data},
		bubbles: true,
		cancelable: true,
	}));
}

/** @returns {string} */
function browserName() {
	const userAgent = navigator.userAgent;
	if (/Edg\//.test(userAgent)) return 'edge';
	if (/Firefox\//.test(userAgent)) return 'firefox';
	if (/Chrome\/|CriOS\//.test(userAgent)) return 'chrome';
	if (/Safari\//.test(userAgent)) return 'safari';
	return 'other';
}

/**
 * @param {string} value
 * @returns {Uint8Array<ArrayBuffer>}
 */
function urlBase64ToUint8Array(value) {
	const padding = '='.repeat((4 - value.length % 4) % 4);
	const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
	const raw = atob(base64);
	const output = new Uint8Array(raw.length);
	for (let index = 0; index < raw.length; index += 1) {
		output[index] = raw.charCodeAt(index);
	}
	return output;
}

/** @param {number} milliseconds */
function delay(milliseconds) {
	return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

/**
 * Firefox can resolve Notification.requestPermission() before the new push
 * permission has propagated to PushManager. Wait briefly for the two APIs to
 * agree before creating the subscription.
 *
 * @param {ServiceWorkerRegistration} registration
 * @param {PushSubscriptionOptionsInit} options
 * @returns {Promise<void>}
 */
async function waitForPushPermission(registration, options) {
	if (typeof registration.pushManager.permissionState !== 'function') return;

	for (let attempt = 0; attempt < 10; attempt += 1) {
		const state = await registration.pushManager.permissionState(options);
		if (state === 'granted') return;
		if (state === 'denied') {
			throw new DOMException('Push notification permission was denied.', 'NotAllowedError');
		}
		await delay(50);
	}
}

/**
 * Firefox can occasionally return AbortError while its push permission or
 * transport is settling. Reuse a subscription created in the meantime, or
 * retry once after a bounded delay.
 *
 * @param {ServiceWorkerRegistration} registration
 * @param {PushSubscriptionOptionsInit} options
 * @returns {Promise<PushSubscription>}
 */
async function createPushSubscription(registration, options) {
	try {
		return await registration.pushManager.subscribe(options);
	} catch (runtimeError) {
		if (!(runtimeError instanceof DOMException)
			|| runtimeError.name !== 'AbortError'
			|| Notification.permission !== 'granted') {
			throw runtimeError;
		}

		const existing = await registration.pushManager.getSubscription();
		if (existing) return existing;

		await delay(250);
		return registration.pushManager.subscribe(options);
	}
}

/**
 * @param {PushSubscription} subscription
 * @returns {string}
 */
function encodeSubscription(subscription) {
	const data = subscription.toJSON();
	if (!data.endpoint || !data.keys?.auth || !data.keys?.p256dh) {
		throw new Error('StarAtlas Push: incomplete browser subscription.');
	}

	return btoa(JSON.stringify({
		endpoint: data.endpoint,
		auth: data.keys.auth,
		p256dh: data.keys.p256dh,
	}));
}

/** @returns {Promise<ServiceWorkerRegistration>} */
async function ensureServiceWorker() {
	if (serviceWorkerRegistration) {
		return serviceWorkerRegistration;
	}

	serviceWorkerRegistration = await navigator.serviceWorker.register(
		config.serviceWorkerUrl,
		{scope: config.serviceWorkerScope},
	);
	return serviceWorkerRegistration;
}

/** @returns {Promise<ServiceWorkerRegistration | null>} */
async function getExistingServiceWorker() {
	if (serviceWorkerRegistration) {
		return serviceWorkerRegistration;
	}

	serviceWorkerRegistration = await navigator.serviceWorker.getRegistration(config.serviceWorkerScope) ?? null;
	return serviceWorkerRegistration;
}

/**
 * @param {PushSubscription} subscription
 * @param {boolean} notifyLifecycle
 * @returns {Promise<void>}
 */
async function syncSubscription(subscription, notifyLifecycle = true) {
	const token = encodeSubscription(subscription);
	const platform = browserName();
	setCookie(COOKIE_DEVICE, token, config.cookies.deviceDays);

	if (notifyLifecycle) {
		emit('smp_got_token', {token: atob(token), platform});
		emit('smp_save_subscription', {token: atob(token), platform});
	}

	const body = new FormData();
	body.set('appid', config.appId);
	body.set('smart_push_api_version', '2');
	body.set('smart_push_device_token', token);
	body.set('smart_push_device_type', platform);
	body.set('refresh_gps', '0');

	const response = await fetch(config.signupEndpoint, {
		method: 'POST',
		body,
		credentials: 'omit',
		mode: 'cors',
	});

	if (!response.ok) {
		throw new Error(`StarAtlas Push: subscription API returned ${response.status}.`);
	}

	/** @type {{respond?: number | string}} */
	const result = await response.json();
	if (!result.respond || Number(result.respond) <= 0) {
		throw new Error('StarAtlas Push: subscription API rejected the request.');
	}

	setCookie(COOKIE_SUBSCRIBER, String(result.respond), config.cookies.subscriberDays);
	setCookie(COOKIE_REQUEST, 'true', config.cookies.requestSubscribedDays);
	if (notifyLifecycle) {
		emit('smp_finish_subscription', {token: atob(token), platform});
	}
}

/** @returns {Promise<PushSubscription | null>} */
async function subscribe() {
	if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
		return null;
	}

	// Keep the permission request directly associated with the user gesture.
	let permission = Notification.permission;
	if (permission === 'default') {
		permission = await Notification.requestPermission();
	}
	if (permission !== 'granted') {
		setCookie(COOKIE_REQUEST, 'true', config.cookies.requestDeniedDays);
		return null;
	}

	const options = {
		userVisibleOnly: true,
		applicationServerKey: urlBase64ToUint8Array(config.applicationServerKey),
	};
	const registration = await ensureServiceWorker();
	let subscription = await registration.pushManager.getSubscription();
	if (subscription) {
		if (!getCookie(COOKIE_SUBSCRIBER)) {
			await syncSubscription(subscription, false);
		}
		return subscription;
	}

	await waitForPushPermission(registration, options);
	subscription = await createPushSubscription(registration, options);
	await syncSubscription(subscription);
	return subscription;
}

function injectStyles() {
	if (document.getElementById(STYLE_ID)) return;
	const style = document.createElement('style');
	style.id = STYLE_ID;
	style.textContent = `
#${MODAL_ID}{direction:ltr;position:fixed;top:0;left:50%;transform:translateX(-50%);display:block;width:600px;max-width:87%;box-sizing:border-box;background:#fff;border:1px solid #e7e7e7;font-family:"Helvetica Neue",Helvetica,Arial,sans-serif;padding:17px;border-radius:5px;text-align:center;overflow:hidden;z-index:10000;box-shadow:0 2px 12px rgba(0,0,0,.12)}
#${MODAL_ID}:focus{outline:2px solid #8bc34a;outline-offset:2px}
#${MODAL_ID} .spns-push-title{margin:23px 0 0;color:#797979;font-size:18px;font-weight:300;line-height:normal}
#${MODAL_ID} .spns-push-note{margin:16px 0;color:#797979;font-size:15px;font-weight:300;line-height:normal}
#${MODAL_ID} .spns-push-actions{text-align:center}
#${MODAL_ID} button{border:0;box-shadow:none;color:#fff;font-size:17px;font-weight:500;border-radius:5px;padding:10px 32px;margin:5px;cursor:pointer}
#${MODAL_ID} button:focus-visible{outline:3px solid #111;outline-offset:2px}
#${MODAL_ID} button:disabled{cursor:wait;opacity:.7}
#smart_push_smio_not_allow{background:#9e9e9e}
#smart_push_smio_allow{background:#8bc34a}
#${MODAL_ID} .spns-push-error{display:none;margin:10px 0 0;color:#b42318;font-size:13px}
@media(max-width:540px){#${MODAL_ID}{width:calc(100% - 20px);max-width:none;padding:12px}#${MODAL_ID} button{padding:10px 24px}}
`;
	document.head.appendChild(style);
}

/** @param {boolean} dismissed */
function closeModal(dismissed = false) {
	if (!modalElement) return;
	modalElement.removeEventListener('keydown', handleModalKeydown);
	modalElement.remove();
	modalElement = null;
	if (dismissed) emit('smp_popup_dismissed', {});
	previousFocus?.focus?.({preventScroll: true});
	previousFocus = null;
}

/** @param {KeyboardEvent} event */
function handleModalKeydown(event) {
	if (event.key !== 'Escape') return;
	setCookie(COOKIE_REQUEST, 'true', config.cookies.requestDeniedDays);
	closeModal(true);
}

function showModal() {
	if (modalElement || getCookie(COOKIE_REQUEST) === 'true') return;
	injectStyles();
	previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

	const modal = document.createElement('section');
	modal.id = MODAL_ID;
	modal.tabIndex = -1;
	modal.setAttribute('role', 'dialog');
	modal.setAttribute('aria-modal', 'false');
	modal.setAttribute('aria-labelledby', 'spns-push-title');
	modal.setAttribute('aria-describedby', 'spns-push-note');
	modal.innerHTML = `
		<p class="spns-push-title" id="spns-push-title"></p>
		<p class="spns-push-note" id="spns-push-note"></p>
		<div class="spns-push-actions">
			<button type="button" id="smart_push_smio_not_allow"></button>
			<button type="button" id="smart_push_smio_allow"></button>
		</div>
		<p class="spns-push-error" id="spns-push-error" role="status"></p>`;

	const title = modal.querySelector('#spns-push-title');
	const note = modal.querySelector('#spns-push-note');
	const denyButton = /** @type {HTMLButtonElement | null} */ (modal.querySelector('#smart_push_smio_not_allow'));
	const allowButton = /** @type {HTMLButtonElement | null} */ (modal.querySelector('#smart_push_smio_allow'));
	const error = /** @type {HTMLElement | null} */ (modal.querySelector('#spns-push-error'));
	if (!title || !note || !denyButton || !allowButton || !error) return;

	title.textContent = config.modal.title;
	note.textContent = config.modal.note;
	denyButton.textContent = config.modal.denyLabel;
	allowButton.textContent = config.modal.allowLabel;

	denyButton.addEventListener('click', () => {
		setCookie(COOKIE_REQUEST, 'true', config.cookies.requestDeniedDays);
		closeModal(true);
	}, {once: true});

	allowButton.addEventListener('click', async () => {
		allowButton.disabled = true;
		denyButton.disabled = true;
		allowButton.textContent = config.modal.savingLabel;
		try {
			const subscription = await subscribe();
			if (subscription) {
				closeModal();
				return;
			}
			closeModal(true);
		} catch (runtimeError) {
			console.error('StarAtlas Push subscription failed.', runtimeError);
			error.textContent = config.modal.errorLabel;
			error.style.display = 'block';
			allowButton.disabled = false;
			denyButton.disabled = false;
			allowButton.textContent = config.modal.allowLabel;
		}
	});

	modal.addEventListener('keydown', handleModalKeydown);
	document.body.appendChild(modal);
	modalElement = modal;
	modal.focus({preventScroll: true});
	emit('smp_popup_init', {});
}

function scheduleModal() {
	window.setTimeout(() => {
		if (typeof window.requestIdleCallback === 'function') {
			window.requestIdleCallback(showModal, {timeout: config.modal.idleTimeoutMs});
			return;
		}
		setTimeout(showModal, 0);
	}, config.modal.delayMs);
}

async function initialize() {
	if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) return;

	try {
		const registration = await getExistingServiceWorker();
		const existing = registration ? await registration.pushManager.getSubscription() : null;
		if (existing) {
			const token = encodeSubscription(existing);
			setCookie(COOKIE_DEVICE, token, config.cookies.deviceDays);
			if (!getCookie(COOKIE_SUBSCRIBER)) {
				await syncSubscription(existing, false);
			}
			return;
		}

		if (getCookie(COOKIE_DEVICE)) deleteCookie(COOKIE_DEVICE);
		if (getCookie(COOKIE_SUBSCRIBER)) deleteCookie(COOKIE_SUBSCRIBER);
		if (Notification.permission === 'denied') {
			setCookie(COOKIE_REQUEST, 'true', config.cookies.requestDeniedDays);
			return;
		}
		if (getCookie(COOKIE_REQUEST) !== 'true') scheduleModal();
	} catch (runtimeError) {
		console.error('StarAtlas Push initialization failed.', runtimeError);
	}
}

function scheduleInitialization() {
	const initializeRuntime = () => void initialize();
	if (typeof window.requestIdleCallback === 'function') {
		window.requestIdleCallback(initializeRuntime, {timeout: config.modal.idleTimeoutMs});
		return;
	}
	window.setTimeout(initializeRuntime, 0);
}

globalWindow.spns_getCookie = getCookie;
globalWindow.smpush_subscribe = subscribe;
globalWindow.spns_requestPushPermission = async () => {
	if (config.messages.subscribeWithUs) window.alert(config.messages.subscribeWithUs);
	return subscribe();
};

if (document.readyState === 'complete') {
	scheduleInitialization();
} else {
	window.addEventListener('load', scheduleInitialization, {once: true});
}
